import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalOverflow from '@mui/joy/ModalOverflow';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import CircularProgress from '@mui/joy/CircularProgress';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';

import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import AvatarWithStatus from './AvatarWithStatus';
import { MOTION, ellipsis, iconColor } from './chatTheme';

/**
 * «Кто посмотрел» — список прочитавших сообщение и поставивших реакции.
 *
 * Данные берём с сервера в момент открытия: список прочитавших меняется
 * постоянно, и держать его в ленте — лишний вес на каждое сообщение.
 * Автор в списке не значится: своё сообщение он не «просматривал».
 */
export default function MessageViewsModal({ open, data, loading, onClose }) {
  const views = data?.views || [];
  const reactions = data?.reactions || [];
  const total = data?.recipients_total ?? 0;

  const when = (iso) => {
    if (!iso) return '';
    try {
      return format(new Date(iso), 'd MMM, HH:mm', { locale: ru });
    } catch (e) {
      return '';
    }
  };

  const Row = ({ person, right }) => (
    <Box
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.25,
        px: 1, py: 0.75, borderRadius: '12px',
        '&:hover': { backgroundColor: 'background.level1' },
      }}
    >
      <AvatarWithStatus size="sm" name={person.name} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography level="body-sm" sx={{ ...ellipsis, fontWeight: 600 }}>
          {person.name}
        </Typography>
        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
          {person.kind === 'client' ? 'Клиент' : 'Сотрудник'}
        </Typography>
      </Box>
      {right && (
        <Typography level="body-xs" sx={{ color: 'text.tertiary', flexShrink: 0 }}>
          {right}
        </Typography>
      )}
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose} sx={{ zIndex: 12100 }}>
      {/* Анимация внутри, а не на ModalDialog: framer перетирал бы
          translate(-50%,-50%), которым Joy центрует окно. */}
      <ModalOverflow sx={{ p: { xs: 1.5, sm: 2 } }}>
      <ModalDialog sx={{
        width: 380, maxWidth: '94vw', borderRadius: '18px', p: 2,
        position: 'relative', top: 'auto', left: 'auto', my: 'auto',
      }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: MOTION.base, ease: MOTION.ease }}
        >
        <Stack direction="row" sx={{ alignItems: 'center', mb: 1.25 }} spacing={1}>
          <Typography
            level="title-sm"
            startDecorator={<VisibilityRoundedIcon sx={{ fontSize: 18 }} />}
            sx={{ fontWeight: 800, flex: 1 }}
          >
            Просмотры
          </Typography>
          <IconButton
            size="sm"
            variant="plain"
            onClick={onClose}
            sx={(theme) => ({
              borderRadius: '50%',
              ...iconColor(theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.62)' : 'rgba(71,84,103,0.72)'),
            })}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {loading ? (
          <Stack alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size="sm" />
          </Stack>
        ) : (
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {reactions.length > 0 && (
              <Box sx={{ mb: 1.5 }}>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5, fontWeight: 700 }}>
                  Реакции
                </Typography>
                {reactions.map((r) => (
                  <Box key={r.emoji} sx={{ mb: 0.5 }}>
                    <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.25 }}>
                      <Box component="span" sx={{ fontSize: 15, mr: 0.5 }}>{r.emoji}</Box>
                      {r.count}
                    </Typography>
                    {(r.users || []).map((u) => (
                      <Row key={`${r.emoji}-${u.kind}-${u.id}`} person={u} />
                    ))}
                  </Box>
                ))}
              </Box>
            )}

            <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5, fontWeight: 700 }}>
              {views.length > 0
                ? `Прочитали ${views.length} из ${total}`
                : 'Пока никто не прочитал'}
            </Typography>

            {views.map((p) => (
              <Row key={`${p.kind}-${p.id}`} person={p} right={when(p.read_at)} />
            ))}

            {views.length === 0 && reactions.length === 0 && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center', py: 2 }}>
                Сообщение ещё никто не открыл
              </Typography>
            )}
          </Box>
        )}
        </Box>
      </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}
